import { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Label } from "./ui/label";
import {
  Upload,
  Download,
  FileSpreadsheet,
  FileText,
  AlertCircle,
  CheckCircle,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  downloadGroupImportTemplate,
  importGroupsFromFile,
  deleteProjectFileById,
  GroupImportFormat,
} from "../api/groupsApi";
import { Alert, AlertDescription } from "./ui/alert";
import { AxiosError } from "axios";

interface ImportGroupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  apiProject?: { status: string; group_file_s3_key?: string };
  onImportSuccess?: () => void;
  onImportError?: (error: any) => void;
}

export default function ImportGroupDialog({
  isOpen,
  onClose,
  projectId,
  apiProject,
  onImportSuccess,
  onImportError,
}: ImportGroupDialogProps) {
  const [format, setFormat] = useState<GroupImportFormat>("csv");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [existingFileDeleted, setExistingFileDeleted] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if there's an existing file that hasn't been deleted
  const hasExistingFile = apiProject?.group_file_s3_key && !existingFileDeleted;

  // Extract filename from S3 key (get the last part after the last slash)
  const getFileNameFromS3Key = (s3Key: string) => {
    return s3Key.split("/").pop() || s3Key;
  };

  // Handle showing delete confirmation
  const handleDeleteExistingFile = () => {
    setShowDeleteConfirmation(true);
  };

  // Handle actual deletion after confirmation
  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteProjectFileById(projectId);

      setExistingFileDeleted(true);
      setSelectedFile(null);
      setImportResult(null);
      setShowDeleteConfirmation(false);

      toast.success(
        "Group file deleted successfully. You can now upload a new file."
      );

      // Call onImportSuccess to refresh the data in parent component
      if (onImportSuccess) {
        await onImportSuccess();
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(
        error,
        "Failed to delete group file"
      );
      toast.error(errorMessage);
      setShowDeleteConfirmation(false); // Close dialog even on error
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle canceling deletion
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  // Centralized error message extraction
  const getErrorMessage = (error: unknown, defaultMessage: string): string => {
    if (error instanceof AxiosError) {
      const response = error.response;
      if (response?.data) {
        if (response.data?.data?.message) {
          return response.data.data.message;
        }
        if (response.data?.message) {
          return response.data.message;
        }
      }
      if (error.message) {
        return error.message;
      }
    }
    if (error instanceof Error) {
      return error.message || defaultMessage;
    }
    return defaultMessage;
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloading(true);
      const blob = await downloadGroupImportTemplate(projectId, format);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `group_import_template.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(
        `Template downloaded successfully as ${format.toUpperCase()}`
      );
    } catch (error: any) {
      const errorMessage = getErrorMessage(
        error,
        "Failed to download template"
      );
      toast.error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const validateFile = (file: File): boolean => {
    const isCSV = format === "csv";
    const validExtensions = isCSV ? [".csv"] : [".xlsx", ".xls"];
    const fileName = file.name.toLowerCase();

    const isValid = validExtensions.some((ext) => fileName.endsWith(ext));

    if (!isValid) {
      toast.error(
        `Invalid file type. Please upload a ${format.toUpperCase()} file.`
      );
    }

    return isValid;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (validateFile(file)) {
      setSelectedFile(file);
      setImportResult(null);
    } else {
      event.target.value = "";
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (validateFile(file)) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleUploadAndImport = async () => {
    if (!selectedFile && !hasExistingFile) {
      toast.error("Please select a file to upload");
      return;
    }

    // If there's an existing file but no new file selected, show a message for now
    if (!selectedFile && hasExistingFile) {
      toast.info(
        "Using existing group file. In a real implementation, this would import the existing file."
      );
      return;
    }

    try {
      setIsUploading(true);
      const result = await importGroupsFromFile(projectId, selectedFile!);

      if (result.isSuccess) {
        setImportResult({ ...result, success: true });

        const groupsCreated = result.data?.groupsCreated || 0;
        const membersAdded = result.data?.membersAdded || 0;

        toast.success(
          `Group import successful! ${groupsCreated} group(s) created with ${membersAdded} member(s).`,
          {
            duration: 4000,
          }
        );

        if (onImportSuccess) {
          await onImportSuccess();
        }

        setTimeout(() => {
          handleClose();
        }, 2500);
      } else {
        throw new Error(result.message || "Import failed with unknown error");
      }
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, "Failed to import groups");
      toast.error(errorMessage, {
        duration: 5000,
      });

      setImportResult({
        success: false,
        message: errorMessage,
      });

      onImportError?.(error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImportResult(null);
    setIsDragging(false);
    setExistingFileDeleted(false);
    setShowDeleteConfirmation(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-bits-blue-800">
              Import Groups
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Format Selection */}
            <div className="space-y-3">
              <Label className="text-base font-medium">
                Select File Format
              </Label>
              <RadioGroup
                value={format}
                onValueChange={(value) => setFormat(value as GroupImportFormat)}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2 border rounded-lg p-4 flex-1 cursor-pointer hover:bg-gray-50">
                  <RadioGroupItem value="csv" id="csv" />
                  <Label
                    htmlFor="csv"
                    className="flex items-center gap-2 cursor-pointer flex-1"
                  >
                    <FileText className="h-5 w-5 text-green-600" />
                    <div>
                      <div className="font-medium">CSV</div>
                      <div className="text-xs text-gray-500">
                        Comma-separated values
                      </div>
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 border rounded-lg p-4 flex-1 cursor-pointer hover:bg-gray-50">
                  <RadioGroupItem value="xlsx" id="xlsx" />
                  <Label
                    htmlFor="xlsx"
                    className="flex items-center gap-2 cursor-pointer flex-1"
                  >
                    <FileSpreadsheet className="h-5 w-5 text-green-700" />
                    <div>
                      <div className="font-medium">Excel</div>
                      <div className="text-xs text-gray-500">
                        Microsoft Excel format
                      </div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Download Template */}
            <div className="space-y-3">
              <Label className="text-base font-medium">Download Template</Label>
              <Button
                onClick={handleDownloadTemplate}
                disabled={isDownloading}
                variant="outline"
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                {isDownloading
                  ? "Downloading..."
                  : `Download ${format.toUpperCase()} Template`}
              </Button>
              <p className="text-sm text-gray-500">
                Download the template file with enrolled students to fill in
                group assignments
              </p>
            </div>

            {/* Upload File */}
            <div className="space-y-3">
              <Label className="text-base font-medium">
                Upload Completed Template
              </Label>

              {hasExistingFile ? (
                // Show existing file with delete option
                <div className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900">
                          {getFileNameFromS3Key(apiProject.group_file_s3_key!)}
                        </p>
                        <p className="text-sm text-gray-500">
                          Previously uploaded group file
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteExistingFile}
                      disabled={isDeleting}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ) : (
                // Show drag and drop interface
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                    isDragging
                      ? "border-bits-blue bg-blue-50"
                      : "hover:border-bits-blue"
                  } ${
                    isUploading
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={() => !isUploading && fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={format === "csv" ? ".csv" : ".xlsx,.xls"}
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={isUploading}
                  />
                  <Upload
                    className={`h-12 w-12 mx-auto mb-4 ${
                      isDragging ? "text-bits-blue" : "text-gray-400"
                    }`}
                  />
                  <div className="text-sm font-medium text-gray-700 mb-1">
                    {selectedFile
                      ? selectedFile.name
                      : isDragging
                      ? "Drop file here"
                      : "Click to upload or drag and drop"}
                  </div>
                  <div className="text-xs text-gray-500">
                    {format === "csv"
                      ? "CSV files only"
                      : "Excel files (.xlsx, .xls)"}
                  </div>
                </div>
              )}
            </div>

            {/* Import Result */}
            {importResult && (
              <div className="space-y-3">
                {importResult.success || importResult.isSuccess ? (
                  <Alert className="border-green-200 bg-green-50">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      <div className="font-medium mb-2">
                        {importResult.message || "Import successful!"}
                      </div>
                      {importResult.data && (
                        <div className="text-sm space-y-1">
                          <div>
                            Groups Created: {importResult.data.groupsCreated}
                          </div>
                          <div>
                            Members Added: {importResult.data.membersAdded}
                          </div>
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">
                      <div className="font-medium mb-2">
                        {importResult.message || "Import failed"}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isUploading}
            >
              Cancel
            </Button>
            {(!hasExistingFile || selectedFile) && (
              <Button
                variant="bits"
                onClick={handleUploadAndImport}
                disabled={!selectedFile || isUploading}
                className="bg-bits-blue hover:bg-bits-blue/90"
              >
                {isUploading ? "Importing..." : "Upload & Import"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-red-600">
              Delete Group File
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-700">
              Are you sure you want to delete the group? This will remove all
              group configurations and cannot be undone.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelDelete}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Yes, Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
